/**
 * SalesClientsModule Component
 * Manages sales clients in the Sales module
 * Extracted from App.js for better maintainability
 */
import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SalesClientsModule = ({ onBack }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [clientForm, setClientForm] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: 'ON',
    postal_code: '',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales/clients`);
      setClients(response.data);
      setLoadError('');
    } catch (error) {
      if (error.response?.status === 401) {
        return;
      }
      console.error('Error fetching clients:', error);
      setLoadError('Unable to refresh customers. Please reload.');
    } finally {
      setLoading(false);
    }
  };

  const openClientModal = (client = null) => {
    if (client) {
      setClientForm({
        id: client.id,
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        province: client.province || 'ON',
        postal_code: client.postal_code || '',
        notes: client.notes || ''
      });
    } else {
      setClientForm({ id: '', name: '', email: '', phone: '', address: '', city: '', province: 'ON', postal_code: '', notes: '' });
    }
    setShowClientModal(true);
  };

  const handleSaveClient = async () => {
    if (!clientForm.name) {
      alert('Client name is required.');
      return;
    }
    try {
      if (clientForm.id) {
        await axios.put(`${API}/sales/clients/${clientForm.id}`, clientForm);
      } else {
        // Don't send empty id — let backend generate UUID
        const { id, ...payload } = clientForm;
        await axios.post(`${API}/sales/clients`, payload);
      }
      await fetchClients();
      setShowClientModal(false);
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Unable to save client.');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await axios.delete(`${API}/sales/clients/${clientId}`);
      await fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Unable to delete client.');
    }
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-end">
          <button
            onClick={() => openClientModal()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-semibold"
            data-testid="sales-clients-new"
          >
            New Customer
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        <input
          type="text"
          placeholder="Search customers by name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-6"
          data-testid="sales-clients-search"
        />
        {loadError && (
          <div className="text-xs text-red-600 mb-4" data-testid="sales-clients-load-error">
            {loadError}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-600">Loading customers...</div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 text-gray-600" data-testid="sales-clients-empty">No customers found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredClients.map(client => (
              <div key={client.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`sales-client-card-${client.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800" data-testid={`sales-client-name-${client.id}`}>{client.name}</h3>
                    <p className="text-sm text-gray-600" data-testid={`sales-client-email-${client.id}`}>{client.email}</p>
                    <p className="text-sm text-gray-600">{client.phone}</p>
                    <p className="text-xs text-gray-500 mt-2">{client.address}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => openClientModal(client)}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                      data-testid={`sales-client-edit-${client.id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.id)}
                      className="px-3 py-1.5 border border-red-300 text-red-600 rounded-md text-sm"
                      data-testid={`sales-client-delete-${client.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="sales-client-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">{clientForm.id ? 'Edit Customer' : 'New Customer'}</h3>
              <button
                onClick={() => setShowClientModal(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="sales-client-modal-close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Name"
                value={clientForm.name}
                onChange={(e) => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="sales-client-form-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={clientForm.email}
                onChange={(e) => setClientForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="sales-client-form-email"
              />
              <input
                type="text"
                placeholder="Phone"
                value={clientForm.phone}
                onChange={(e) => setClientForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="sales-client-form-phone"
              />
              <input
                type="text"
                placeholder="Address"
                value={clientForm.address}
                onChange={(e) => setClientForm(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="sales-client-form-address"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="City"
                  value={clientForm.city}
                  onChange={(e) => setClientForm(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="sales-client-form-city"
                />
                <select
                  value={clientForm.province}
                  onChange={(e) => setClientForm(prev => ({ ...prev, province: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="sales-client-form-province"
                >
                  <option value="ON">Ontario</option>
                  <option value="AB">Alberta</option>
                  <option value="BC">British Columbia</option>
                  <option value="MB">Manitoba</option>
                  <option value="NB">New Brunswick</option>
                  <option value="NL">Newfoundland and Labrador</option>
                  <option value="NS">Nova Scotia</option>
                  <option value="PE">Prince Edward Island</option>
                  <option value="QC">Quebec</option>
                  <option value="SK">Saskatchewan</option>
                </select>
                <input
                  type="text"
                  placeholder="Postal Code"
                  value={clientForm.postal_code}
                  onChange={(e) => setClientForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="sales-client-form-postal"
                />
              </div>
              <textarea
                rows={3}
                placeholder="Notes"
                value={clientForm.notes}
                onChange={(e) => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                data-testid="sales-client-form-notes"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowClientModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                  data-testid="sales-client-form-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveClient}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold"
                  data-testid="sales-client-form-save"
                >
                  Save Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesClientsModule;
